import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/LanguageContext';
import {
  LayoutDashboard, Building, Users, Mail,
  LogOut, Home, ChevronRight, UserCircle, Settings, Info, Briefcase, Tag, FileBarChart2,
  Globe, ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import AiChatPanel from '../../components/AiChatPanel';

const LOGO_URL = "/logo.png";

export default function AdminLayout() {
  const { user, logout, isAdmin, isManager, isEditor } = useAuth();
  const { t, lang, switchLang, SUPPORTED_LANGS } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);

  const navSections = [
    {
      titleKey: 'admin.main',
      items: [
        { href: '/admin', icon: LayoutDashboard, labelKey: 'admin.dashboard', exact: true }
      ]
    },
    {
      titleKey: 'admin.site',
      items: [
        ...((isAdmin || isManager || isEditor) ? [
          { href: '/admin/settings', icon: Settings, labelKey: 'admin.settings' },
        ] : []),
        ...((isAdmin || isManager || isEditor) ? [
          { href: '/admin/properties', icon: Building, labelKey: 'admin.projects' },
        ] : []),
        { href: '/admin/listings', icon: Tag, labelKey: 'admin.listings_nav' },
        ...((isAdmin || isManager || isEditor) ? [
          { href: '/admin/services', icon: Briefcase, labelKey: 'admin.services_nav' },
          { href: '/admin/about', icon: Info, labelKey: 'admin.about_nav' },
        ] : [])
      ]
    },
    {
      titleKey: 'admin.contacts_section',
      items: [
        // Contacts: Admin + Manager only
        ...((isAdmin || isManager) ? [
          { href: '/admin/contacts', icon: Users, labelKey: 'admin.contacts_nav' },
        ] : []),
        // Inquiries: ALL roles see this
        { href: '/admin/inquiries', icon: Mail, labelKey: 'admin.inquiries_nav' },
        // Reports: Admin + Manager only
        ...((isAdmin || isManager) ? [
          { href: '/admin/reports', icon: FileBarChart2, labelKey: 'admin.reports_nav' }
        ] : []),
      ]
    },
    ...((isAdmin || isManager || isEditor) ? [{
      titleKey: 'admin.administration',
      items: [
        { href: '/admin/users', icon: UserCircle, labelKey: 'admin.users_nav' }
      ]
    }] : [])
  ];

  const allNavItems = navSections.flatMap(s => s.items);

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const currentPage = allNavItems.find(item => isActive(item.href, item.exact));
  const currentPageLabel = currentPage ? t(currentPage.labelKey) : t('admin.panel');

  const currentLang = SUPPORTED_LANGS.find(l => l.code === lang);

  const roleLabels = {
    admin: { bg: 'Администратор', en: 'Administrator', tr: 'Yönetici', el: 'Διαχειριστής' },
    manager: { bg: 'Мениджър', en: 'Manager', tr: 'Müdür', el: 'Διευθυντής' },
    editor: { bg: 'Редактор', en: 'Editor', tr: 'Editör', el: 'Συντάκτης' },
    broker: { bg: 'Брокер', en: 'Broker', tr: 'Acente', el: 'Μεσίτης' },
  };

  const roleLabel = roleLabels[user?.role]?.[lang] || roleLabels[user?.role]?.['bg'] || user?.role;

  return (
    <div className="admin-layout" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar flex flex-col bg-[#1B5E37]">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="GreenCastle" className="h-10 w-auto brightness-0 invert" />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navSections.map((section, sIdx) => (
            <div key={sIdx} className="mb-4">
              <h3 className="px-6 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider">
                {t(section.titleKey)}
              </h3>
              {section.items.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center gap-3 px-6 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-colors ${isActive(item.href, item.exact) ? 'bg-white/20 text-white border-r-4 border-white' : ''}`}
                  data-testid={`admin-nav-${item.labelKey}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm">{t(item.labelKey)}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-4 py-2 mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold text-white">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-white">{user?.name}</p>
              <p className="text-xs text-white/60 capitalize">{roleLabel}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-3" />
            {lang === 'bg' ? 'Изход от системата' : lang === 'en' ? 'Sign Out' : lang === 'tr' ? 'Çıkış Yap' : 'Αποσύνδεση'}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen bg-gray-50">
        {/* Header */}
        <header className="h-16 bg-white border-b border-border flex items-center justify-between px-8 shadow-sm">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-[#1B5E37] flex items-center gap-1 transition-colors">
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">{t('admin.view_site')}</span>
            </Link>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-foreground">{currentPageLabel}</span>
          </div>

          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="font-medium">{currentLang?.label}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {langDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-border z-50 py-1">
                  <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border mb-1">
                    {t('admin.language')}
                  </div>
                  {SUPPORTED_LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { switchLang(l.code); setLangDropdownOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                        lang === l.code
                          ? 'bg-[#1B5E37]/10 text-[#1B5E37] font-semibold'
                          : 'text-foreground hover:bg-secondary'
                      }`}
                    >
                      <span className="text-base">{l.code === 'bg' ? '🇧🇬' : l.code === 'en' ? '🇬🇧' : l.code === 'tr' ? '🇹🇷' : '🇬🇷'}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* View Site Button */}
            <Link to="/" target="_blank">
              <Button variant="outline" size="sm" className="border-[#1B5E37] text-[#1B5E37] hover:bg-[#1B5E37] hover:text-white">
                {t('admin.preview')}
              </Button>
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6 lg:p-8">
          <Outlet />
        </main>

        {/* AI Chat Panel */}
        <AiChatPanel />
      </div>

      {/* Close dropdown on outside click */}
      {langDropdownOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setLangDropdownOpen(false)} />
      )}
    </div>
  );
}
